import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Body from './Body';
import Header from './Header';
import * as Utils from './utils';
import { modeToNumber } from './utils';

const Calendar = props => {
    const events = props.events;
    const style = props.style ?? {};
    const height = props.height;
    const mode = props.mode ?? 'week';
    const locale = props.locale ?? 'en';
    const eventCellStyle = props.eventCellStyle;
    const date = props.date;
    const scrollOffsetMinutes = props.scrollOffsetMinutes ?? 0;
    const swipeEnabled = props.swipeEnabled ?? true;
    const weekStartsOn = props.weekStartsOn ?? 0;
    const showTime = props.showTime ?? true;
    const onEventPress = props.onEventPress;
    const onChangeDate = props.onChangeDate;
    const onCellPress = props.onCellPress;
    const onNavigate = props.onNavigate;
    const startHour = props.startHour ?? 4;
    const endHour = props.endHour ?? 22;
    const [targetDate, setTargetDate] = useState(date);

    const onSwipeHorizontal = useCallback(
        direction => {
            if (!swipeEnabled) {
                return;
            }

            if (direction === 'LEFT') {
                setTargetDate(targetDate.add(modeToNumber(mode), 'day'));
            } else {
                setTargetDate(targetDate.add(modeToNumber(mode) * -1, 'day'));
            }
        },
        [swipeEnabled, targetDate, mode]
    );

    const dateRange = useMemo(() => {
        switch (mode) {
            case '3days':
                return Utils.getDatesInNextThreeDays(targetDate, locale);
            case 'week':
                return Utils.getDatesInWeek(targetDate, weekStartsOn, locale);
            case 'day':
                return Utils.getDatesInNextOneDay(targetDate, locale);
            default:
                throw new Error('undefined mode');
        }
    }, [mode, targetDate, locale, weekStartsOn]);

    useEffect(() => {
        if (date) {
            setTargetDate(date);
        }
    }, [date]);

    useEffect(() => {
        if (onChangeDate) {
            onChangeDate([dateRange[0].toDate(), dateRange.slice(-1)[0].toDate()]);
        }
    }, [dateRange, onChangeDate]);

    const allDayEvents = useMemo(() => events.filter(x => Utils.isAllDayEvent(x)), [events]);
    const daytimeEvents = useMemo(() => events.filter(x => !Utils.isAllDayEvent(x)), [events]);
    const cellHeight = useMemo(() => Math.max(height - 30, Utils.MIN_HEIGHT) / 24, [height]);

    const commonProps = {
        cellHeight: cellHeight,
        dateRange: dateRange,
        mode: mode,
        style: style
    };

    return (
        <>
            <Header {...commonProps} allDayEvents={allDayEvents} onNavigate={e => onNavigate(e)} onEventPress={onEventPress} onSwipeHorizontal={onSwipeHorizontal} />
            <Body
                {...commonProps}
                endHour={endHour}
                events={daytimeEvents}
                containerHeight={height}
                onEventPress={onEventPress}
                onCellPress={onCellPress}
                eventCellStyle={eventCellStyle}
                scrollOffsetMinutes={scrollOffsetMinutes}
                showTime={showTime}
                startHour={startHour}
                onSwipeHorizontal={onSwipeHorizontal}
            />
        </>
    );
};

Calendar.propTypes = {
    events: PropTypes.arrayOf(Object),
    style: PropTypes.object,
    height: PropTypes.number,
    mode: PropTypes.string,
    locale: PropTypes.string,
    endHour: PropTypes.number,
    eventCellStyle: PropTypes.oneOf([PropTypes.object, PropTypes.func]),
    date: PropTypes.instanceOf(Date),
    scrollOffsetMinutes: PropTypes.number,
    swipeEnabled: PropTypes.bool,
    weekStartsOn: PropTypes.number,
    showTime: PropTypes.bool,
    onEventPress: PropTypes.func,
    onChangeDate: PropTypes.func,
    onCellPress: PropTypes.func,
    setSelectedDate: PropTypes.func,
    startHour: PropTypes.number
};

export default React.memo(Calendar);
